/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Payload, VU } from '@tivocorp/tivo-webapp-loggers';
import { SourceType } from 'client-code/channels-service';
import { ScreenType } from 'client-code/previews';

import { IDLE_GRID_GUIDE_DURATION_IN_SECOND } from '../../../config';
import { Channel } from '../../../core/entities/Channel';
import { GuideType, PCState } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { Log } from '../../../core/logging/Log';
import { QoEAppLogger } from '../../../core/logging/qoe/QoEAppLogger';
import { VuLogger } from '../../../core/logging/VULogger';
import { CursorManager, KeyManager } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls/PlaybackDataModelImpl';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import {
    TChannelV2,
    TGridGuideRow,
    TGuideCell,
    TOfferPreviewCustomData,
} from '../../../core/types';
import { DateTime, UniqueIdentifier } from '../../../core/utils';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { launchVodScreen } from '../../jumpchannel/jumpchannel.reducers';
import { showChannelOptionsOverlay } from '../../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import { showNoNetworkOverlay } from '../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import {
    showPCPinOverlayWithProgramRating,
    updatePcState,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showRecordOverlay } from '../../overlay/recordoverlay/recordoverlay.reducers';
import { showSidebarNavigationMenu } from '../../sidebarnavigationmenu/sidebarnavigationmenu.reducers';
import {
    startVideoPlayback,
    triggerPlaybackOnPinVerify,
} from '../../videoplayer/videoplayer.reducers';
import {
    setEnteredChannelNumber,
    startEnterChannelNumber,
} from '../enterchannelnumber/enterchannelnumber.reducers';
import {
    guideHideSpinner,
    loadGridGuideNextChannels,
    loadGridGuidePreviousChannels,
} from '../guide.reducers';
import { setVerticalAndHorizontalPositionAction } from '../guidehighlight/guidehighlight.actions';
import {
    setHorizontalPosition,
    setVerticalPosition,
} from '../guidehighlight/guidehighlight.reducers';
import { onLeftMainGuideOperations, onRightMainGuideOperations } from './mainguide.reducers';
import { onLeftPastGuideOperations, onRightPastGuideOperations } from './pastguide.reducers';

const TAG = 'guidekeyoperations';

const getCurrentOfferPreview = async (
    guideCell: TGuideCell,
    offerId: string,
    offerPreview: TOfferPreviewCustomData,
): Promise<TOfferPreviewCustomData> => {
    let currentOfferPreview = offerPreview;
    if (guideCell.offerId !== offerId) {
        const service = new ServicesClientImpl();
        const res = await service.fetchOfferPreview(guideCell.offerId, ScreenType.GuideHeader);
        currentOfferPreview = res.offerPreview;
    }
    return currentOfferPreview;
};

const checkProgramAndInvokeChannelOverlayOrRecordOverlay =
    (showMetaData?: boolean) => async (dispatch: Function, getState: TGetState) => {
        const { gridGuide, guideHighlight } = getState();
        const { channels, guideRows, isChannelsServiceFail, offerId, offerPreview } = gridGuide;
        const { verticalPosition, horizontalPosition } = guideHighlight;

        if (isChannelsServiceFail) return;

        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(showNoNetworkOverlay());
            return;
        }

        const channel: Channel = channels[verticalPosition];

        if (horizontalPosition === 0) {
            dispatch(showChannelOptionsOverlay(channel, GuideType.GRID_GUIDE));
            Log.w(TAG, 'Channel options overlay will be called here.');
        } else if (horizontalPosition) {
            const guideCells: TGuideCell[] = guideRows[verticalPosition]?.guideCells;
            if (guideCells?.length) {
                // horizontalPosition has 1 additional position of channel, -1 to get program index
                const guideCell: TGuideCell = guideCells[horizontalPosition - 1];
                const { isAdult } = guideCell;

                const currentOfferPreview: TOfferPreviewCustomData = await getCurrentOfferPreview(
                    guideCell,
                    offerId,
                    offerPreview,
                );

                const services = new ServicesClientImpl();
                if (isAdult && services.shouldHideMetadata()) {
                    const boundedOnKeyOkAdultProgramCell = onKeyOkAdultProgramCell.bind(
                        null,
                        showMetaData,
                    );
                    dispatch(
                        showPCPinOverlayWithProgramRating(boundedOnKeyOkAdultProgramCell, isAdult),
                    );
                    return;
                }

                dispatch(
                    showRecordOverlay({
                        channel,
                        guideCell,
                        offerPreview: currentOfferPreview,
                        showMetaData,
                    }),
                );
            }
        }
    };

export const cancelGuideSpinnerOperations = () => (dispatch: Function) => {
    const recordingSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
    );
    const guideCellData = recordingSharedPreferenceEditor.getJson(
        SharedPrefConstants.RECORDING_GUIDECELL_INFO,
    ) as unknown as any;
    if (guideCellData) {
        const services = new ServicesClientImpl();
        services
            .getRecordingOfferIdOptionsRequestModel(guideCellData.contentId, guideCellData.offerId)
            .drop();
        services
            .getRecordingOfferIdScheduleRequestModel(guideCellData.contentId, guideCellData.offerId)
            .drop();
    }
    UniqueIdentifier.getNextActionsNetworkCallId();
    UniqueIdentifier.getNextOttActionDetailsNetworkCallId();
    UniqueIdentifier.getNextOnePassServiceNetworkCallId();
    dispatch(guideHideSpinner());
};

export const onKeyRightGuideOperations = () => (dispatch: Function, getState: TGetState) => {
    const { gridGuide, guideHighlight } = getState();
    const {
        isPastGuideMode,
        isChannelsServiceFail,
        isGuideRowsServiceFail,
        currentTimestamp,
        guideRows,
        futureGuideRows,
        pastGuideRows,
    } = gridGuide;
    const { horizontalPosition } = guideHighlight;

    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );

    if (isChannelsServiceFail || !hasValidGuideRows) return;

    if (isGuideRowsServiceFail && horizontalPosition === 1) return;

    if (isPastGuideMode) {
        dispatch(onRightPastGuideOperations());
    } else {
        dispatch(onRightMainGuideOperations());
    }
};

export const onKeyLeftGuideOperations = () => (dispatch: Function, getState: TGetState) => {
    const { guideHighlight, gridGuide } = getState();
    const {
        isPastGuideMode,
        isChannelsServiceFail,
        isGuideRowsServiceFail,
        currentTimestamp,
        pastGuideRows,
        guideRows,
        futureGuideRows,
    } = gridGuide;

    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );
    const { horizontalPosition } = guideHighlight;

    if (isChannelsServiceFail || !hasValidGuideRows) return;

    if (isGuideRowsServiceFail && horizontalPosition === 0) return;

    if (!horizontalPosition) dispatch(setHorizontalPosition(0));

    if (isPastGuideMode) {
        dispatch(onLeftPastGuideOperations());
    } else {
        dispatch(onLeftMainGuideOperations());
    }
};

export const onKeyDownGuideOperations = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition, horizontalPosition, horizontalSession } = getState().guideHighlight;
    const {
        LIMIT,
        guideRows,
        currentTimestamp,
        channels,
        isChannelsServiceFail,
        futureGuideRows,
        pastGuideRows,
    } = getState().gridGuide;

    let newVerticalPosition = verticalPosition;

    const isLessThanAPage = channels.length < LIMIT;
    let nextVerticalPosition = verticalPosition + 1;
    if (isLessThanAPage && nextVerticalPosition >= channels.length) {
        nextVerticalPosition = 0;
        newVerticalPosition = -1;
    }

    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );

    if (isChannelsServiceFail) return;
    if (horizontalPosition > 0 && !hasValidGuideRows) return;
    let loadNextChannels: boolean = false;
    if (channels && channels.length !== 0 && horizontalPosition === 0) {
        if (nextVerticalPosition === LIMIT) {
            loadNextChannels = true;
        } else {
            dispatch(setVerticalPosition(nextVerticalPosition));
        }
    } else if (guideRows && guideRows.length !== 0 && guideRows[newVerticalPosition + 1]) {
        const { guideCells } = guideRows[newVerticalPosition + 1];
        const currentIndex = evaluateCurrentHorizontalSessionIndex(
            guideCells,
            currentTimestamp,
            horizontalSession,
        );
        const nextHorizontalPosition = guideCells && guideCells.length === 1 ? 1 : currentIndex + 1;
        dispatch(
            setVerticalAndHorizontalPositionAction(nextVerticalPosition, nextHorizontalPosition),
        );
    } else {
        loadNextChannels = true;
    }

    if (loadNextChannels) {
        dispatch(loadGridGuideNextChannels());
    }
};

export const onKeyUpGuideOperations = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition, horizontalPosition, horizontalSession } = getState().guideHighlight;
    const {
        guideRows,
        currentTimestamp,
        channels,
        isChannelsServiceFail,
        futureGuideRows,
        pastGuideRows,
        LIMIT,
    } = getState().gridGuide;

    let newVerticalPosition = verticalPosition;

    const isLessThanAPage = channels.length < LIMIT;
    let previousVerticalPosition = verticalPosition - 1;
    if (isLessThanAPage && previousVerticalPosition < 0) {
        previousVerticalPosition = channels.length - 1;
        newVerticalPosition = 1;
    }

    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );
    if (isChannelsServiceFail) return;
    if (horizontalPosition > 0 && !hasValidGuideRows) return;
    let loadPreviousChannels: boolean = false;

    if (channels && channels.length !== 0 && horizontalPosition === 0) {
        if (previousVerticalPosition < 0) {
            loadPreviousChannels = true;
        } else {
            dispatch(setVerticalPosition(previousVerticalPosition));
        }
    } else if (guideRows && guideRows.length !== 0 && guideRows[newVerticalPosition - 1]) {
        const { guideCells } = guideRows[newVerticalPosition - 1];
        const currentIndex = evaluateCurrentHorizontalSessionIndex(
            guideCells,
            currentTimestamp,
            horizontalSession,
        );
        const nextHorizontalPosition = guideCells && guideCells.length === 1 ? 1 : currentIndex + 1;
        dispatch(
            setVerticalAndHorizontalPositionAction(
                previousVerticalPosition,
                nextHorizontalPosition,
            ),
        );
    } else {
        loadPreviousChannels = true;
    }

    if (loadPreviousChannels) {
        dispatch(loadGridGuidePreviousChannels());
    }
};

export const onKeyOkGuideOperations = () => async (dispatch: Function, getState: TGetState) => {
    const oKeyManager = KeyManager.getInstance();
    oKeyManager.startKeyIgnoring();

    const { channels, guideRows, isChannelsServiceFail, offerId, offerPreview } =
        getState().gridGuide;
    const { verticalPosition, horizontalPosition } = getState().guideHighlight;

    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showNoNetworkOverlay());
        return;
    }

    if (isChannelsServiceFail) return;

    const channel: Channel | TChannelV2 = channels[verticalPosition];

    if (horizontalPosition === 0) {
        dispatch(showChannelOptionsOverlay(channel, GuideType.GRID_GUIDE));
        Log.w(TAG, 'Channel options overlay will be called here.');
    } else {
        const guideCells: TGuideCell[] = guideRows[verticalPosition]?.guideCells;
        if (
            guideCells.length > 0 &&
            channel.sourceType === SourceType.JumpChannel &&
            channel.channelNumber
        ) {
            dispatch(launchVodScreen(channel.channelNumber));
            Log.i(TAG, 'Jump channel program cell, launching VOD screen.');
        } else if (guideCells?.length > 0) {
            const currentTime = new DateTime();
            // Current-Horizontal-Position 0 means "Focus is on channel cell"
            const currentHorizontalPosition = horizontalPosition === 0 ? 0 : horizontalPosition - 1;
            const guideCell: TGuideCell = guideCells[currentHorizontalPosition];
            const { startTime, duration, isAdult } = guideCell;
            const endTime = startTime.addSeconds(duration);

            const isCurrentlyAiring =
                currentTime.getTime() >= startTime.getTime() &&
                currentTime.getTime() < endTime.getTime();
            const livePlayable: IPlayable = {
                channel,
                playbackContentType: PlaybackContentType.LIVE,
            };
            if (isCurrentlyAiring) {
                const payload: Payload = VuLogger.createPayload(guideCell, channel, offerPreview);
                VU.itemSelectedEvent(payload);
                const playbackModelImpl = PlaybackDataModelImpl.getInstance();
                if (playbackModelImpl.getPlaybackContentType() !== PlaybackContentType.LIVE) {
                    VideoPlaybackController.getInstance().updateLastViewedPosition();
                    playbackModelImpl.resetPlaybackData();
                }
                if (channel.channelNumber !== QoEAppLogger.lastTunedChannel) {
                    QoEAppLogger.setWatchContentEndReason(
                        QoEAppLogger.PlaybackEndReason
                            .USER_MOVE_TO_LIVE_TV_FROM_GUIDE_AND_CHANNEL_WAS_CHANGED,
                    );
                }
                if (
                    channel.channelNumber !==
                    VideoPlaybackController.getInstance().getPlayable()?.channel?.channelNumber
                ) {
                    VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
                }
                dispatch(startVideoPlayback(livePlayable));
                return;
            }

            const services = new ServicesClientImpl();
            if (isAdult && services.shouldHideMetadata()) {
                dispatch(showPCPinOverlayWithProgramRating(onKeyOkAdultProgramCell, isAdult));
                return;
            }

            const currentOfferPreview: TOfferPreviewCustomData = await getCurrentOfferPreview(
                guideCell,
                offerId,
                offerPreview,
            );
            dispatch(showRecordOverlay({ channel, guideCell, offerPreview: currentOfferPreview }));
        }
    }

    Log.i(TAG, `OK_KEY`);
    Log.i(TAG, '', channels[verticalPosition]);
    oKeyManager.stopKeyIgnoring();
};

const onKeyOkAdultProgramCell =
    (showMetaData?: boolean) => async (dispatch: Function, getState: TGetState) => {
        const { channels, guideRows, offerId, offerPreview } = getState().gridGuide;
        const { verticalPosition, horizontalPosition } = getState().guideHighlight;
        const guideCells: TGuideCell[] = guideRows[verticalPosition]?.guideCells;
        const currentHorizontalPosition = horizontalPosition === 0 ? 0 : horizontalPosition - 1;
        const guideCell: TGuideCell = guideCells[currentHorizontalPosition];

        const channel: Channel = channels[verticalPosition];
        const currentOfferPreview: TOfferPreviewCustomData = await getCurrentOfferPreview(
            guideCell,
            offerId,
            offerPreview,
        );

        dispatch(updatePcState(PCState.Unlocked));
        dispatch(triggerPlaybackOnPinVerify());
        dispatch(
            showRecordOverlay({
                channel,
                guideCell,
                offerPreview: currentOfferPreview,
                showMetaData,
            }),
        );
    };

export const onKeyPlayGuideOperations = () => (dispatch: Function) => {
    if (CursorManager.isCursorAppeared()) return;
    dispatch(onKeyOkGuideOperations());
};

export const onLongKeyOkGuideOperations = () => (dispatch: Function) => {
    dispatch(checkProgramAndInvokeChannelOverlayOrRecordOverlay());
};

export const onKeyBackGuideOperations = () => (dispatch: Function) => {
    dispatch(showSidebarNavigationMenu());
};

export const onKeyInfoGuideOperations = () => (dispatch: Function) => {
    dispatch(checkProgramAndInvokeChannelOverlayOrRecordOverlay(true));
};

export const onKeyDigitGuideOperations = () => (dispatch: Function, getState: TGetState) => {
    const { isEnterChannelNumberActive, enteredChannelNumber } = getState().enterChannelNumber;

    if (!isEnterChannelNumberActive) dispatch(startEnterChannelNumber());
    dispatch(setEnteredChannelNumber());

    Log.i(TAG, enteredChannelNumber);
};

export const onKeyDashGuideOperations = () => (dispatch: Function, getState: TGetState) => {
    const { isEnterChannelNumberActive } = getState().enterChannelNumber;
    if (!isEnterChannelNumberActive) {
        dispatch(startEnterChannelNumber());
    }
};

export const evaluateCurrentHorizontalSessionIndex = (
    guideCells: TGuideCell[],
    currentTimestamp: DateTime,
    horizontalSession: DateTime,
): number => {
    return guideCells?.findIndex((guideCell: TGuideCell) => {
        const { startTime, duration } = guideCell;
        const _startTime =
            startTime?.getTime() < currentTimestamp.getTime() ? currentTimestamp : startTime;
        const _endTime = startTime.addSeconds(duration);

        return (
            horizontalSession.getTime() >= _startTime?.getTime() &&
            horizontalSession.getTime() < _endTime?.getTime()
        );
    });
};

const isValidGuideRows = (
    pastGuideRows: TGridGuideRow[],
    guideRows: TGridGuideRow[],
    futureGuideRows: TGridGuideRow[],
    currentTimestamp: DateTime,
): boolean => {
    const hasNotValidGuideRows: boolean =
        !guideRows ||
        (guideRows && guideRows.length === 0) ||
        !futureGuideRows ||
        (futureGuideRows && futureGuideRows.length === 0) ||
        !pastGuideRows ||
        (pastGuideRows && pastGuideRows.length === 0);
    if (hasNotValidGuideRows) return !hasNotValidGuideRows;

    const { startTime, duration } = guideRows[0].guideCells[0];
    const ProgramEndTime = startTime.addSeconds(duration);
    const guideEndTime = currentTimestamp.addSeconds(IDLE_GRID_GUIDE_DURATION_IN_SECOND);

    if (
        startTime.getTime() === currentTimestamp.getTime() ||
        ProgramEndTime.getTime() <= guideEndTime.getTime() ||
        (startTime.getTime() < currentTimestamp.getTime() &&
            ProgramEndTime.getTime() >= guideEndTime.getTime())
    )
        return true;
    return false;
};
